import type {
  AccessCatalogItemPublic,
  AccessPasswordHandling,
  AccessRecordPublic,
  AuditEntryPublic,
  UserPublic,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';

import { STR } from '../../../i18n';
import {
  ApiError,
  createAccessRecord,
  createGrant,
  createOnBehalfAccessRequest,
  listAccessCatalog,
  listAccessRecords,
  listAudit,
  listGrants,
  listUsers,
  removeAccessRecord,
  retireAccessRecord,
  type SettableAccessRecordStatus,
  updateAccessRecord,
  type UpdateAccessRecordBody,
} from '../../../lib/api';
import { formatDateTime, formatMetadataValue, humanizeKey } from '../../../lib/format';
import { queryKeys } from '../../../lib/queryKeys';
import { useSession } from '../../../lib/session';
import { showToast } from '../../../lib/toast';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../../ui/Modal';
import { Callout, Chip, EmptyRow, Tile } from '../../../ui/Primitives';
import {
  eyebrowStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  monoStyle,
  quietButtonStyle,
  textFieldStyle,
} from '../../../ui/styles';

import {
  itemLabel,
  passwordHandlingLabel,
  pathLabel,
  RECORD_STATUS_TONE,
  recordStatusLabel,
} from './accessFormat';
import {
  addAccessSubmission,
  hrMustRequest,
  whatOptionLabel,
  whatOptions,
  whatValue,
} from './addAccessOptions';

/**
 * THE ACCESS-RECORD DIALOGS, shared by the person page, the catalog drawer and the Matrix,
 * so a record opens, edits and shows its History the same way from every surface.
 *
 * NO PASSWORD, EVER: a login is an email or username, and the only password fact here is
 * HOW it is handled (Managed / Given raw). Retire, never delete: "Mark removed" records that
 * IT took the access away; "Retire" is only for a record entered by mistake. Everything is an
 * affordance over the server, which enforces HR's narrower scope (an HR actor may set a
 * record Active only on rows flagged "HR can grant directly") and answers with its own words.
 */

const LIST_LIMIT = 200;

const SETTABLE: readonly SettableAccessRecordStatus[] = [
  'active',
  'needs_access',
  'requested',
  'remove_access',
];

const PASSWORD_MODES: readonly AccessPasswordHandling[] = ['managed', 'given_raw'];

/** Every access write touches counts elsewhere (Queue, Matrix, person page): refresh them all. */
function useAccessRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
  };
}

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : STR.errors.generic;
}

/** The actor is an administrator (HR is not): only administrators can read the audit log. */
function useIsAdminActor(): boolean {
  const session = useSession();
  return session.data?.isAdmin === true;
}

/** Names for the ids the record carries (`addedBy`, `removedBy`). Same read as Users. */
function useUserNames(): Map<string, string> {
  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  return useMemo(
    () => new Map((usersQuery.data?.data ?? []).map((user) => [user.id, user.name])),
    [usersQuery.data],
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="tx-muted" style={fieldLabelStyle}>
        {label}
      </span>
      {children}
    </label>
  );
}

function PasswordSelect({
  value,
  onChange,
}: {
  value: AccessPasswordHandling | '';
  onChange: (value: AccessPasswordHandling | '') => void;
}) {
  return (
    <Field label={STR.access.record.passwordLabel}>
      <select
        className="field"
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === 'managed' || next === 'given_raw' ? next : '');
        }}
        style={textFieldStyle}
      >
        {value === '' ? <option value="">{STR.access.record.passwordPick}</option> : null}
        {PASSWORD_MODES.map((mode) => (
          <option key={mode} value={mode}>
            {`${passwordHandlingLabel(mode)} — ${STR.accessCommon.passwordHandlingHint[mode]}`}
          </option>
        ))}
      </select>
      <span className="tx-subtle" style={{ ...hintStyle, marginTop: 4 }}>
        {STR.access.record.passwordHint}
      </span>
    </Field>
  );
}

/* ------------------------------------------------------------------- history -- */

/** One audit row as a change line: old → new for a field change, else what was done. */
function changeText(entry: AuditEntryPublic): string {
  const meta = entry.metadata;
  if (meta === null) return STR.common.dash;
  const rawField = meta.field;
  const field = typeof rawField === 'string' ? rawField : null;
  if (field !== null) {
    const names: Record<string, string> = STR.access.record.fieldNames;
    const label = names[field] ?? humanizeKey(field);
    return STR.access.record.historyChange(
      label,
      formatMetadataValue(meta.old),
      formatMetadataValue(meta.new),
    );
  }
  if (entry.action === 'access_records.create') return STR.access.record.historyCreated;
  return STR.common.dash;
}

/**
 * A target's History: its audit rows, newest first. ADMIN-ONLY by the server's gate on
 * `/api/admin/audit` — callers render this only for an administrator, since HR's read would
 * be refused (spec row 4 asks for HR too; that needs a server-side read, flagged on SIR-7513).
 */
export function HistoryList({ targetId }: { targetId: string }) {
  const historyQuery = useQuery({
    queryKey: queryKeys.accessHistory(targetId),
    queryFn: () => listAudit({ targetId, limit: 50 }),
  });

  if (historyQuery.isLoading) {
    return <EmptyRow>{STR.common.loading}</EmptyRow>;
  }
  if (historyQuery.isError) {
    return <EmptyRow>{STR.access.record.historyFailed}</EmptyRow>;
  }
  const rows = historyQuery.data?.data ?? [];
  if (rows.length === 0) return <EmptyRow>{STR.access.record.historyEmpty}</EmptyRow>;

  return (
    <div className="dscroll">
      <table className="dtable">
        <thead>
          <tr>
            <th>{STR.access.record.historyWhen}</th>
            <th>{STR.access.record.historyWho}</th>
            <th>{STR.access.record.historyWhat}</th>
            <th>{STR.access.record.historyChangeCol}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => (
            <tr key={entry.id}>
              <td className="tx-subtle" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {formatDateTime(entry.createdAt)}
              </td>
              <td style={{ fontSize: 12.5 }}>{entry.actor?.name ?? STR.access.record.system}</td>
              <td>
                <Chip tone="neutral" size="sm" style={monoStyle}>
                  {entry.action}
                </Chip>
              </td>
              <td style={{ fontSize: 12.5 }}>{changeText(entry)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------- record dialog -- */

type RecordStage = 'view' | 'edit' | 'confirmRetire';

/**
 * One record: view (with its History, for administrators), edit, mark granted / removed,
 * retire. The record shown is the snapshot the caller passed; after a write the lists
 * refresh and the dialog closes, so it never shows a stale value as current.
 *
 * `readOnly` (the view-only Catalog, SIR-7864): the record view and Close, nothing else.
 * No Mark granted / removed, no Edit, and the edit and retire stages cannot be reached.
 */
export function RecordDialog({
  record,
  startInEdit = false,
  readOnly = false,
  onClose,
}: {
  record: AccessRecordPublic;
  /** Open straight on the edit form (a row's Edit action) rather than the record view. */
  startInEdit?: boolean;
  /** A view-only reader: every write is gone, whatever the record's status. */
  readOnly?: boolean;
  onClose: () => void;
}) {
  const refresh = useAccessRefresh();
  const isAdminActor = useIsAdminActor();
  const names = useUserNames();
  const live = record.status !== 'removed' && record.retiredAt === null;
  // `writable` gates every write affordance below; a read-only reader never leaves the view.
  const writable = live && !readOnly;
  const [requestedStage, setStage] = useState<RecordStage>(
    startInEdit && writable ? 'edit' : 'view',
  );
  const stage: RecordStage = readOnly ? 'view' : requestedStage;
  const [error, setError] = useState<string | null>(null);

  const [login, setLogin] = useState(record.login ?? '');
  const [caseLogin, setCaseLogin] = useState(record.caseLogin ?? '');
  const [notes, setNotes] = useState(record.notes ?? '');
  const [status, setStatus] = useState<SettableAccessRecordStatus>(
    record.status === 'removed' ? 'active' : record.status,
  );
  const [mode, setMode] = useState<AccessPasswordHandling>(record.passwordHandling);

  const title = STR.access.record.viewTitle(record.catalogItem.name, record.user.name);
  const place = pathLabel(record.path);
  const sharedNames = record.sharedWith.map((user) => user.name).join(', ');

  const updateMutation = useMutation({
    mutationFn: (body: UpdateAccessRecordBody) => updateAccessRecord(record.id, body),
    onSuccess: (_result, body) => {
      refresh();
      if (Object.keys(body).length === 1 && body.status === 'active') {
        showToast(STR.access.record.granted(record.catalogItem.name));
      } else {
        showToast(STR.access.record.saved(Object.keys(body).length));
      }
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeAccessRecord(record.id),
    onSuccess: () => {
      refresh();
      showToast(STR.access.record.removed(record.catalogItem.name), 'warn');
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const retireMutation = useMutation({
    mutationFn: () => retireAccessRecord(record.id),
    onSuccess: () => {
      refresh();
      showToast(STR.access.record.retired(record.catalogItem.name), 'warn');
      onClose();
    },
    onError: (failure) => {
      setStage('edit');
      setError(errorText(failure));
    },
  });

  const busy = updateMutation.isPending || removeMutation.isPending || retireMutation.isPending;

  /** Only what changed — the server writes one History row per changed field. */
  function buildPatch(): UpdateAccessRecordBody {
    const patch: UpdateAccessRecordBody = {};
    const nextLogin = login.trim() === '' ? null : login.trim();
    const nextCase = caseLogin.trim() === '' ? null : caseLogin.trim();
    const nextNotes = notes.trim() === '' ? null : notes.trim();
    if (nextLogin !== record.login) patch.login = nextLogin;
    if (nextCase !== record.caseLogin) patch.caseLogin = nextCase;
    if (nextNotes !== record.notes) patch.notes = nextNotes;
    if (status !== record.status) patch.status = status;
    if (mode !== record.passwordHandling) patch.passwordHandling = mode;
    return patch;
  }

  function save() {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      setError(STR.access.record.unchanged);
      return;
    }
    setError(null);
    updateMutation.mutate(patch);
  }

  const errorCallout =
    error === null ? null : (
      <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
        {error}
      </Callout>
    );

  if (stage === 'confirmRetire') {
    return (
      <Modal
        title={STR.access.record.retireTitle}
        onClose={() => {
          setStage('edit');
        }}
        footer={
          <>
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              onClick={() => {
                setStage('edit');
              }}
            >
              {STR.common.cancel}
            </button>
            <button
              type="button"
              className="dbtn"
              style={modalButtonStyle}
              disabled={busy}
              onClick={() => {
                retireMutation.mutate();
              }}
            >
              {STR.access.record.retireSubmit}
            </button>
          </>
        }
      >
        <ModalProse>
          <ModalParagraph>{STR.access.record.retireBody}</ModalParagraph>
        </ModalProse>
      </Modal>
    );
  }

  if (stage === 'edit') {
    return (
      <Modal
        title={STR.access.record.editTitle(record.catalogItem.name)}
        lead={`${record.user.name}, ${place}. ${STR.access.record.editLead}`}
        onClose={onClose}
        footer={
          <>
            <button
              type="button"
              className="tbtn tbtn-danger"
              style={{ ...quietButtonStyle, marginRight: 'auto' }}
              disabled={busy}
              onClick={() => {
                setError(null);
                setStage('confirmRetire');
              }}
            >
              {STR.access.record.retire}
            </button>
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              onClick={() => {
                setError(null);
                setStage('view');
              }}
            >
              {STR.common.cancel}
            </button>
            <button
              type="button"
              className="prim"
              style={modalButtonStyle}
              disabled={busy}
              onClick={save}
            >
              {STR.access.record.save}
            </button>
          </>
        }
      >
        <ModalForm>
          {errorCallout}
          <Field label={STR.access.record.loginLabel}>
            <input
              className="field"
              type="text"
              value={login}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={STR.access.record.loginPlaceholder}
              onChange={(event) => {
                setLogin(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </Field>
          {record.sharedWith.length > 0 && record.login !== null ? (
            <Callout tone="neutral">
              {STR.access.record.sharedEditCallout(record.login, sharedNames, record.user.name)}
            </Callout>
          ) : null}
          <PasswordSelect
            value={mode}
            onChange={(next) => {
              if (next !== '') setMode(next);
              setError(null);
            }}
          />
          <Field label={STR.access.record.caseLoginLabel}>
            <input
              className="field"
              type="text"
              value={caseLogin}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={STR.access.record.caseLoginPlaceholder}
              onChange={(event) => {
                setCaseLogin(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </Field>
          <Field label={STR.access.record.statusLabel}>
            <select
              className="field"
              value={status}
              onChange={(event) => {
                const next = SETTABLE.find((value) => value === event.target.value);
                if (next !== undefined) setStatus(next);
                setError(null);
              }}
              style={textFieldStyle}
            >
              {SETTABLE.map((value) => (
                <option key={value} value={value}>
                  {recordStatusLabel(value)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={STR.access.record.notesLabel}>
            <textarea
              className="field"
              rows={3}
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
                setError(null);
              }}
              style={{ ...textFieldStyle, resize: 'vertical' }}
            />
          </Field>
        </ModalForm>
      </Modal>
    );
  }

  return (
    <Modal
      title={title}
      lead={place}
      width="min(760px,100%)"
      onClose={onClose}
      footer={
        <>
          {writable && record.status === 'needs_access' ? (
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              disabled={busy}
              onClick={() => {
                setError(null);
                updateMutation.mutate({ status: 'active' });
              }}
            >
              {STR.access.record.markGranted}
            </button>
          ) : null}
          {writable && record.status === 'remove_access' ? (
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              disabled={busy}
              onClick={() => {
                setError(null);
                removeMutation.mutate();
              }}
            >
              {STR.access.record.markRemoved}
            </button>
          ) : null}
          {writable ? (
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              onClick={() => {
                setError(null);
                setStage('edit');
              }}
            >
              {STR.access.record.edit}
            </button>
          ) : null}
          <button type="button" className="prim" style={modalButtonStyle} onClick={onClose}>
            {STR.common.close}
          </button>
        </>
      }
    >
      {/* The Modal draws no body padding of its own; this is ModalForm's, for a read view. */}
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {errorCallout}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 10 }}>
          <Tile label={STR.access.record.loginLabel}>
            <span style={{ ...monoStyle, fontSize: 12.5 }}>
              {record.login ?? STR.common.dash}
            </span>
          </Tile>
          <Tile label={STR.access.record.passwordLabel}>
            <span style={{ display: 'block' }}>{passwordHandlingLabel(record.passwordHandling)}</span>
            <span className="tx-subtle" style={{ ...hintStyle, fontWeight: 500 }}>
              {STR.accessCommon.passwordHandlingHint[record.passwordHandling]}
            </span>
          </Tile>
          <Tile label={STR.access.record.caseLoginLabel}>
            <span style={{ ...monoStyle, fontSize: 12.5 }}>
              {record.caseLogin ?? STR.common.dash}
            </span>
          </Tile>
          <Tile label={STR.access.record.statusLabel}>
            <Chip tone={RECORD_STATUS_TONE[record.status]}>{recordStatusLabel(record.status)}</Chip>
          </Tile>
        </div>
        {record.sharedWith.length > 0 ? (
          <Callout tone="neutral">{STR.access.record.sharedCallout(sharedNames)}</Callout>
        ) : null}
        {record.notes === null ? null : (
          <div style={{ fontSize: 12.5 }}>
            <span className="tx-subtle">{STR.access.record.notesLabel}: </span>
            {record.notes}
          </div>
        )}
        <div className="tx-subtle" style={{ fontSize: 12 }}>
          {STR.access.record.addedLine(
            names.get(record.addedBy) ?? STR.common.dash,
            formatDateTime(record.addedAt),
          )}
          {record.removedAt === null ? null : (
            <>
              {STR.accessCommon.pathSeparator}
              {STR.access.record.removedLine(
                record.removedBy === null
                  ? STR.common.dash
                  : (names.get(record.removedBy) ?? STR.common.dash),
                formatDateTime(record.removedAt),
              )}
            </>
          )}
        </div>
        <div className="tx-subtle" style={eyebrowStyle}>
          {STR.access.record.historyTitle}
        </div>
        {/* The History is the audit log, which the server gates to administrators. */}
        {isAdminActor ? (
          <HistoryList targetId={record.id} />
        ) : (
          <div className="tx-subtle" style={{ fontSize: 12.5 }}>
            {STR.access.record.historyAdminsOnly}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- add record -- */

type Status = Extract<SettableAccessRecordStatus, 'active' | 'needs_access'>;

/**
 * A new outside-system record. Person and catalog row are fixed when the caller knows them
 * (the person page knows the person); otherwise they are picked. By default only OUTSIDE-
 * system rows are offered: a suite app's access is a grant, given from Application access as
 * today. The password mode must be picked — there is no default to slide past — and no field
 * anywhere takes a password value.
 *
 * `withSuiteApps` (the Matrix, FINAL demo `addAccessModal`): the "What" picker is always
 * shown, preselected to the caller's row, and offers suite apps first. Picking a suite app
 * grants it through the same `createGrant` that Application access uses (additive: nothing
 * else changes); picking an outside system adds a record as above. Administrators only: the
 * grant route is admin-gated, so an HR session is never offered a suite app here.
 */
export function AddRecordDialog({
  userId,
  catalogItemId,
  withSuiteApps = false,
  onClose,
}: {
  userId?: string;
  catalogItemId?: string;
  withSuiteApps?: boolean;
  onClose: () => void;
}) {
  const refresh = useAccessRefresh();
  const queryClient = useQueryClient();
  const session = useSession();
  const hrOnly =
    session.data !== undefined &&
    !session.data.isAdmin &&
    (session.data.isHr || session.data.isIt);
  const offerApps = withSuiteApps && session.data?.isAdmin === true;

  const [personId, setPersonId] = useState(userId ?? '');
  const [itemId, setItemId] = useState(catalogItemId ?? '');
  const [login, setLogin] = useState('');
  const [caseLogin, setCaseLogin] = useState('');
  const [status, setStatus] = useState<Status>('needs_access');
  const [mode, setMode] = useState<AccessPasswordHandling | ''>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  const catalogQuery = useQuery({
    queryKey: queryKeys.accessCatalog(false),
    queryFn: () => listAccessCatalog(),
  });
  // The person's live records, so a row they already hold is not offered twice.
  const heldQuery = useQuery({
    queryKey: queryKeys.accessRecords({ userId: personId, includeRemoved: false }),
    queryFn: () => listAccessRecords({ userId: personId }),
    enabled: personId !== '',
  });
  // Who else holds the item, to say when a typed login is already shared.
  const itemRecordsQuery = useQuery({
    queryKey: queryKeys.accessRecords({ catalogItemId: itemId, includeRemoved: false }),
    queryFn: () => listAccessRecords({ catalogItemId: itemId }),
    enabled: itemId !== '',
  });

  // The person's live grants, so a suite app they already hold is not offered.
  const grantsQuery = useQuery({
    queryKey: queryKeys.grantsForUser(personId, false),
    queryFn: () => listGrants({ userId: personId, includeRevoked: false }),
    enabled: offerApps && personId !== '',
  });

  const people: UserPublic[] = useMemo(
    () => (usersQuery.data?.data ?? []).filter((user) => user.active),
    [usersQuery.data],
  );
  const person = (usersQuery.data?.data ?? []).find((user) => user.id === personId) ?? null;

  const options: AccessCatalogItemPublic[] = useMemo(
    () =>
      whatOptions({
        catalog: catalogQuery.data?.data ?? [],
        heldItemIds: new Set((heldQuery.data?.data ?? []).map((record) => record.catalogItem.id)),
        grantedAppIds: new Set((grantsQuery.data?.data ?? []).map((grant) => grant.applicationId)),
        keepItemId: catalogItemId,
        withSuiteApps: offerApps,
      }),
    [catalogQuery.data, heldQuery.data, grantsQuery.data, catalogItemId, offerApps],
  );
  const item = options.find((candidate) => candidate.id === itemId) ?? null;
  const app = item?.application ?? null;
  // HR on a row it cannot grant files a request for an administrator: no record, no fields.
  const hrBlocked = hrMustRequest(hrOnly, item);
  // With the picker, the record fields follow the pick: none until a row is picked, none for
  // a suite app (a grant has no login). Without it they are always there, as before.
  const recordFields = !hrBlocked && (withSuiteApps ? item !== null && app === null : true);

  const sameLogin = useMemo(() => {
    const needle = login.trim().toLowerCase();
    if (needle === '') return [];
    return (itemRecordsQuery.data?.data ?? [])
      .filter((record) => record.user.id !== personId && record.login?.toLowerCase() === needle)
      .map((record) => record.user.name);
  }, [itemRecordsQuery.data, login, personId]);

  const createMutation = useMutation({
    mutationFn: createAccessRecord,
    onSuccess: (result) => {
      refresh();
      showToast(
        result.record.status === 'needs_access'
          ? STR.access.record.addedQueued(result.record.catalogItem.name)
          : STR.access.record.added(result.record.catalogItem.name),
      );
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const grantMutation = useMutation({
    mutationFn: (body: { userId: string; applicationId: string }) => createGrant(body),
    onSuccess: (result) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
      showToast(STR.toast.accessGranted(result.grant.user.name, result.grant.application.name));
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  const requestMutation = useMutation({
    mutationFn: createOnBehalfAccessRequest,
    onSuccess: () => {
      refresh();
      showToast(STR.access.record.requestSent);
      onClose();
    },
    onError: (failure) => {
      setError(errorText(failure));
    },
  });

  function submit() {
    const submission = addAccessSubmission({
      personId,
      item,
      hrOnly,
      login,
      caseLogin,
      status,
      mode,
      notes,
    });
    if (submission.kind === 'invalid') {
      setError(submission.message);
      return;
    }
    setError(null);
    if (submission.kind === 'grant') grantMutation.mutate(submission.body);
    else if (submission.kind === 'request') requestMutation.mutate(submission.body);
    else createMutation.mutate(submission.body);
  }

  return (
    <Modal
      title={person === null ? STR.access.record.addTitleNoPerson : STR.access.record.addTitle(person.name)}
      lead={withSuiteApps ? STR.access.record.addLeadWithApps : STR.access.record.addLead}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onClose}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={
              createMutation.isPending ||
              grantMutation.isPending ||
              requestMutation.isPending ||
              personId === '' ||
              item === null ||
              (app === null && !hrBlocked && mode === '')
            }
            onClick={submit}
          >
            {hrBlocked
              ? STR.access.record.sendRequest
              : !withSuiteApps
                ? STR.access.record.add
                : item === null
                  ? STR.access.record.addPick
                  : app !== null
                    ? STR.access.record.grantApp(app.name)
                    : STR.access.record.add}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        {userId === undefined ? (
          <Field label={STR.access.record.personLabel}>
            <select
              className="field"
              value={personId}
              onChange={(event) => {
                setPersonId(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            >
              <option value="">{STR.access.record.personPick}</option>
              {people.map((user) => (
                <option key={user.id} value={user.id}>
                  {`${user.name} — ${user.email}`}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {catalogItemId === undefined || withSuiteApps ? (
          <Field label={STR.access.record.itemLabel}>
            <select
              className="field"
              value={whatValue(options, itemId)}
              onChange={(event) => {
                setItemId(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            >
              <option value="">{STR.access.record.itemPick}</option>
              {options.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {whatOptionLabel(candidate)}
                </option>
              ))}
            </select>
            {catalogQuery.isSuccess && options.length === 0 ? (
              <span className="tx-subtle" style={{ ...hintStyle, marginTop: 4 }}>
                {withSuiteApps ? STR.access.record.itemNoneAny : STR.access.record.itemNone}
              </span>
            ) : null}
          </Field>
        ) : item === null ? null : (
          <div style={{ fontSize: 13, fontWeight: 600 }}>{itemLabel(item.path, item.name)}</div>
        )}
        {hrBlocked ? <Callout tone="neutral">{STR.access.record.hrNotDirect}</Callout> : null}
        {recordFields ? (
          <>
          <Field label={STR.access.record.loginLabel}>
            <input
              className="field"
              type="text"
              value={login}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={STR.access.record.loginPlaceholder}
              onChange={(event) => {
                setLogin(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </Field>
          {sameLogin.length > 0 ? (
            <Callout tone="neutral">{STR.access.record.sameLogin(sameLogin.join(', '))}</Callout>
          ) : null}
          <PasswordSelect
            value={mode}
            onChange={(next) => {
              setMode(next);
              setError(null);
            }}
          />
          <Field label={`${STR.access.record.caseLoginLabel} ${STR.access.record.optional}`}>
            <input
              className="field"
              type="text"
              value={caseLogin}
              autoCapitalize="none"
              spellCheck={false}
              placeholder={STR.access.record.caseLoginPlaceholder}
              onChange={(event) => {
                setCaseLogin(event.target.value);
                setError(null);
              }}
              style={textFieldStyle}
            />
          </Field>
          <Field label={STR.access.record.statusLabel}>
            <select
              className="field"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value === 'active' ? 'active' : 'needs_access');
              }}
              style={textFieldStyle}
            >
              <option value="needs_access">{STR.access.record.statusNeedsLong}</option>
              <option value="active">{STR.access.record.statusActiveLong}</option>
            </select>
          </Field>
          <Field label={`${STR.access.record.notesLabel} ${STR.access.record.optional}`}>
            <input
              className="field"
              type="text"
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
              style={textFieldStyle}
            />
          </Field>
          </>
        ) : null}
      </ModalForm>
    </Modal>
  );
}
